import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { acquisitionsApi } from "@/lib/api/acquisitions";
import { Loader2, Save } from "lucide-react";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";

const currentYear = new Date().getFullYear();

export default function FundFormPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    code: "",
    description: "",
    fiscalYear: String(currentYear),
    allocation: "",
    isActive: true,
  });

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    acquisitionsApi.funds
      .getById(id)
      .then((f) => {
        setForm({
          name: f.name,
          code: f.code,
          description: f.description || "",
          fiscalYear: String(f.fiscalYear),
          allocation: String(f.allocation),
          isActive: f.isActive,
        });
      })
      .catch(() => toast.error("Failed to load fund."))
      .finally(() => setLoading(false));
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.code.trim() || !form.allocation) {
      toast.error("Name, code, and allocation are required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim(),
        description: form.description || undefined,
        fiscalYear: parseInt(form.fiscalYear, 10),
        allocation: parseFloat(form.allocation),
        isActive: form.isActive,
      };

      if (isEdit && id) {
        await acquisitionsApi.funds.update(id, payload);
        toast.success("Fund updated.");
      } else {
        await acquisitionsApi.funds.create(payload);
        toast.success("Fund created.");
      }
      navigate("/acquisitions/budget");
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Failed to save fund."));
    } finally {
      setSaving(false);
    }
  };

  const update = (field: string, value: unknown) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  return (
    <PageContainer width="wide">
      <PageHeader
        title={isEdit ? "Edit Fund" : "Create Fund"}
        description={isEdit ? "Update fund details" : "Add a new acquisition budget fund"}
        breadcrumbItems={[
          { label: "Budget", href: "/acquisitions/budget" },
          { label: isEdit ? "Edit Fund" : "Create Fund" },
        ]}
        showSearch={false}
        titleSize="md"
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4 rounded-lg border bg-white p-5 sm:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label required>Fund Name</Label>
              <Input
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="e.g. E-Resources"
              />
            </div>
            <div className="space-y-1.5">
              <Label required>Fund Code</Label>
              <Input
                value={form.code}
                onChange={(e) => update("code", e.target.value.toUpperCase())}
                placeholder="e.g. ERES"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              rows={2}
              placeholder="Optional notes about this fund"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label required>Fiscal Year</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                value={form.fiscalYear}
                onChange={(e) => update("fiscalYear", e.target.value)}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label required>Total Allocation (NGN)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.allocation}
                onChange={(e) => update("allocation", e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Switch checked={form.isActive} onCheckedChange={(v) => update("isActive", v)} />
            <Label>Active</Label>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate("/acquisitions/budget")}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {isEdit ? "Update Fund" : "Create Fund"}
          </Button>
        </div>
      </form>
    </PageContainer>
  );
}
